import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  attendanceLoadLeaveBalances,
  attendanceSaveLeaveBalances,
  type AttendanceLeaveEmployeeRow,
} from '../../lib/apiPhpAdapter'
import { formatEmployeeFullName } from '../../utils/employeeDisplay'
import { comparePersonNames } from '../../utils/personNameSort'
import './AttendanceOrgAdmin.css'

function leaveRowDisplayName(r: AttendanceLeaveEmployeeRow): string {
  return formatEmployeeFullName({
    name: r.employee_name,
    first_name: r.first_name,
    last_name: r.last_name,
    title: r.title,
    title_after: r.title_after,
  })
}

type Props = {
  year: number
  busy: boolean
  setBusy: (v: boolean) => void
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

type Draft = { dov: string; sam: string; nemoc: string }

function LeaveHead({ top, bot }: { top: string; bot: string }) {
  return (
    <span className="att-leave-th-2line">
      <span className="att-leave-th-2line__top">{top}</span>
      <span className="att-leave-th-2line__bot">{bot}</span>
    </span>
  )
}

export function AttendanceLeaveBalancesPanel({ year, busy, setBusy, showAlert }: Props) {
  const [leaveYear, setLeaveYear] = useState(year)
  const [leaveSearch, setLeaveSearch] = useState('')
  const [leaveEmployees, setLeaveEmployees] = useState<AttendanceLeaveEmployeeRow[]>([])
  const [leaveDraft, setLeaveDraft] = useState<Record<number, Draft>>({})
  const [leaveSavingId, setLeaveSavingId] = useState<number | null>(null)

  const syncLeaveDraft = (rows: AttendanceLeaveEmployeeRow[]) => {
    const next: Record<number, Draft> = {}
    for (const r of rows) {
      next[r.school_employee_id] = {
        dov: String(r.dovolena?.remaining ?? 0),
        sam: String(r.samostudium?.remaining ?? 0),
        nemoc: String(r.nemoc?.remaining ?? 0),
      }
    }
    setLeaveDraft(next)
  }

  const reloadLeaveRows = useCallback(async () => {
    const bal = await attendanceLoadLeaveBalances(leaveYear)
    const rows = Array.isArray(bal.employees) ? bal.employees : []
    setLeaveEmployees(rows)
    syncLeaveDraft(rows)
  }, [leaveYear])

  useEffect(() => {
    setLeaveYear(year)
  }, [year])

  useEffect(() => {
    let cancelled = false
    attendanceLoadLeaveBalances(leaveYear)
      .then((bal) => {
        if (cancelled) return
        const rows = Array.isArray(bal.employees) ? bal.employees : []
        setLeaveEmployees(rows)
        syncLeaveDraft(rows)
      })
      .catch((e: unknown) => {
        if (!cancelled) showAlert(e instanceof Error ? e.message : 'Nároky se nepodařilo načíst', 'error')
      })
    return () => {
      cancelled = true
    }
  }, [leaveYear, showAlert])

  const filteredLeaveEmployees = useMemo(() => {
    const q = leaveSearch.trim().toLowerCase()
    const list = !q
      ? [...leaveEmployees]
      : leaveEmployees.filter((r) => {
          const name = leaveRowDisplayName(r).toLowerCase()
          const pn = (r.personal_number || '').toLowerCase()
          return name.includes(q) || pn.includes(q)
        })
    // Řazení podle příjmení (ne podle zobrazení titul+jméno)
    list.sort((a, b) =>
      comparePersonNames(
        { name: a.employee_name, first_name: a.first_name, last_name: a.last_name },
        { name: b.employee_name, first_name: b.first_name, last_name: b.last_name },
      ),
    )
    return list
  }, [leaveEmployees, leaveSearch])

  const saveEmployee = (empId: number) => {
    void (async () => {
      const draft = leaveDraft[empId]
      if (!draft) return
      const dov = parseFloat(String(draft.dov).replace(',', '.'))
      const sam = parseFloat(String(draft.sam).replace(',', '.'))
      const nemoc = parseFloat(String(draft.nemoc).replace(',', '.'))
      if (!Number.isFinite(dov) || dov < 0) {
        showAlert('Zadejte platný zůstatek dovolené (hodiny).', 'info')
        return
      }
      if (!Number.isFinite(sam) || sam < 0) {
        showAlert('Zadejte platný zůstatek samostudia (dny).', 'info')
        return
      }
      if (!Number.isFinite(nemoc) || nemoc < 0) {
        showAlert('Zadejte platný zůstatek nemoci (dny).', 'info')
        return
      }
      setBusy(true)
      setLeaveSavingId(empId)
      try {
        await attendanceSaveLeaveBalances({
          school_employee_id: empId,
          year: leaveYear,
          dovolena: dov,
          samostudium: sam,
          nemoc,
        })
        showAlert('Nároky uloženy', 'success')
        await reloadLeaveRows()
      } catch (e: unknown) {
        showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
      } finally {
        setLeaveSavingId(null)
        setBusy(false)
      }
    })()
  }

  return (
    <div className="att-cat att-leave-panel">
      <p className="att-cat__intro">
        Pro každého zaměstnance: nárok, čerpání a zbývající zůstatek — DOV v hodinách, SAM a NEM ve
        dnech. Upravujete zbývající zůstatek — čerpání se počítá automaticky.
      </p>

      <div className="att-leave-form__filters">
        <label>
          Rok
          <input
            type="number"
            value={leaveYear}
            min={2020}
            max={2100}
            style={{ width: 88 }}
            onChange={(e) => setLeaveYear(parseInt(e.target.value, 10) || year)}
          />
        </label>
        <label className="att-leave-form__search">
          Hledat
          <input
            type="search"
            value={leaveSearch}
            placeholder="jméno / os. číslo"
            onChange={(e) => setLeaveSearch(e.target.value)}
          />
        </label>
        <span className="attendance-muted">
          {filteredLeaveEmployees.length}/{leaveEmployees.length}
        </span>
      </div>

      <div className="att-cat__wrap att-cat__wrap--sticky att-leave-panel__wrap">
        <table className="att-cat__table att-cat__table--leave att-cat__table--leave-fit">
          <thead>
            <tr>
              <th className="att-cat__col-name att-cat__col-name--person">Zaměstnanec</th>
              <th className="att-cat__col-narrow">
                <LeaveHead top="Os." bot="číslo" />
              </th>
              <th className="att-cat__col-narrow">
                <LeaveHead top="DOV h" bot="nárok" />
              </th>
              <th className="att-cat__col-narrow">
                <LeaveHead top="DOV h" bot="čerp." />
              </th>
              <th className="att-cat__col-narrow">
                <LeaveHead top="DOV h" bot="zbývá" />
              </th>
              <th className="att-cat__col-narrow">
                <LeaveHead top="SAM d" bot="nárok" />
              </th>
              <th className="att-cat__col-narrow">
                <LeaveHead top="SAM d" bot="čerp." />
              </th>
              <th className="att-cat__col-narrow">
                <LeaveHead top="SAM d" bot="zbývá" />
              </th>
              <th className="att-cat__col-narrow">
                <LeaveHead top="NEM d" bot="nárok" />
              </th>
              <th className="att-cat__col-narrow">
                <LeaveHead top="NEM d" bot="čerp." />
              </th>
              <th className="att-cat__col-narrow">
                <LeaveHead top="NEM d" bot="zbývá" />
              </th>
              <th className="att-cat__col-actions" aria-label="Akce" />
            </tr>
          </thead>
          <tbody>
            {filteredLeaveEmployees.map((r) => {
              const draft = leaveDraft[r.school_employee_id] ?? {
                dov: String(r.dovolena?.remaining ?? 0),
                sam: String(r.samostudium?.remaining ?? 0),
                nemoc: String(r.nemoc?.remaining ?? 0),
              }
              const dirty =
                Number(draft.dov) !== Number(r.dovolena?.remaining ?? 0) ||
                Number(draft.sam) !== Number(r.samostudium?.remaining ?? 0) ||
                Number(draft.nemoc) !== Number(r.nemoc?.remaining ?? 0)
              const saving = leaveSavingId === r.school_employee_id
              const dovEnt = Number(r.dovolena?.entitled ?? 0) + Number(r.dovolena?.adjust ?? 0)
              const samEnt = Number(r.samostudium?.entitled ?? 0) + Number(r.samostudium?.adjust ?? 0)
              const nemEnt = Number(r.nemoc?.entitled ?? 0) + Number(r.nemoc?.adjust ?? 0)
              return (
                <tr key={r.school_employee_id}>
                  <td
                    className="att-cat__col-name att-cat__col-name--person"
                    title={leaveRowDisplayName(r) || undefined}
                  >
                    {leaveRowDisplayName(r) || '—'}
                  </td>
                  <td className="att-cat__col-narrow">{r.personal_number || '—'}</td>
                  <td className="att-cat__col-narrow">{dovEnt}</td>
                  <td className="att-cat__col-narrow">{r.dovolena?.used ?? 0}</td>
                  <td className="att-cat__col-narrow">
                    <input
                      className="att-leave-inline"
                      type="number"
                      step="0.5"
                      min={0}
                      value={draft.dov}
                      disabled={busy}
                      onChange={(e) =>
                        setLeaveDraft((prev) => ({
                          ...prev,
                          [r.school_employee_id]: { ...draft, dov: e.target.value },
                        }))
                      }
                    />
                  </td>
                  <td className="att-cat__col-narrow">{samEnt}</td>
                  <td className="att-cat__col-narrow">{r.samostudium?.used ?? 0}</td>
                  <td className="att-cat__col-narrow">
                    <input
                      className="att-leave-inline"
                      type="number"
                      step="0.5"
                      min={0}
                      value={draft.sam}
                      disabled={busy}
                      onChange={(e) =>
                        setLeaveDraft((prev) => ({
                          ...prev,
                          [r.school_employee_id]: { ...draft, sam: e.target.value },
                        }))
                      }
                    />
                  </td>
                  <td className="att-cat__col-narrow">{nemEnt}</td>
                  <td className="att-cat__col-narrow">{r.nemoc?.used ?? 0}</td>
                  <td className="att-cat__col-narrow">
                    <input
                      className="att-leave-inline"
                      type="number"
                      step="0.5"
                      min={0}
                      value={draft.nemoc}
                      disabled={busy}
                      onChange={(e) =>
                        setLeaveDraft((prev) => ({
                          ...prev,
                          [r.school_employee_id]: { ...draft, nemoc: e.target.value },
                        }))
                      }
                    />
                  </td>
                  <td className="att-cat__col-actions">
                    <button
                      type="button"
                      className="system-button att-cat__btn"
                      disabled={busy || !dirty}
                      onClick={() => saveEmployee(r.school_employee_id)}
                    >
                      {saving ? '…' : 'Uložit'}
                    </button>
                  </td>
                </tr>
              )
            })}
            {filteredLeaveEmployees.length === 0 ? (
              <tr>
                <td colSpan={12} className="att-cat__empty">
                  {leaveEmployees.length === 0
                    ? 'Žádní zaměstnanci.'
                    : 'Žádný řádek neodpovídá filtru.'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  )
}
